/**
 * Wealthica Account-Position Utilities
 * 
 * This file contains utility functions for organizing and displaying Wealthica accounts and positions.
 * These functions help translate raw API data into formats suitable for UI display
 * and provide consistent formatting and grouping for UI display.
 */

import { isDebugEnabled } from '@/utils/debug';
import { determineStandardAssetClass } from '@/utils/asset-class';

// Core types from official Wealthica API documentation
export interface WealthicaSecurity {
  _id: string;
  currency: string;
  symbol: string;
  type: string;
  name: string;
}

/**
 * Wealthica Investment represents a position's allocation to a specific account
 * The investment.investment field contains the ACCOUNT ID
 * This is the critical link between positions and accounts
 */
export interface WealthicaInvestment {
  institution: string;
  investment: string;  // THIS IS THE ACCOUNT ID
  quantity: number;
  book_value: number;
  market_value: number;
  currency: string;
}

/**
 * Complete Wealthica Position structure based on official API documentation
 * A single position can exist across multiple accounts through the investments array
 * OR be directly associated with a single account
 */
export interface WealthicaPosition {
  _id?: string;
  id?: string;              // Alternative ID field
  security?: WealthicaSecurity;
  class?: string;
  investments?: WealthicaInvestment[];  // Optional: links positions to accounts (complex structure)
  account_id?: string;      // Optional: direct account association (simple structure)
  quantity?: number;        // Aggregate across all accounts or specific to account
  book_value?: number;      // Aggregate across all accounts or specific to account
  market_value?: number;    // Aggregate across all accounts or specific to account
  currency?: string;
  institution?: string;     // Institution name for simple structures
  securityId?: string;
  provider?: string;
  providerName?: string;
}

/**
 * AccountPosition represents a position that has been normalized for a specific account
 * Values like quantity and market_value are taken from the specific investment entry
 * NOT from the root position object
 */
export interface AccountPosition {
  _id?: string;
  id?: string;
  security?: WealthicaSecurity;
  class: string;
  investments?: WealthicaInvestment[];
  quantity?: number;
  book_value?: number;
  market_value?: number;
  currency?: string;
  institution?: string;
  account_id?: string;
  securityId?: string;
  provider?: string;
  providerName?: string;
}

/**
 * AccountData structure for UI display
 * Contains account information and its associated positions
 */
export interface AccountData {
  id: string;
  name: string;
  positions: AccountPosition[];
}

/**
 * AccountPositionsMap can contain two types of entries:
 * 1. By account ID: AccountData objects with positions for that account
 * 2. By position class: Arrays of positions grouped by class (cash, equity, etc.)
 */
export type AccountPositionsMap = Record<string, AccountData | AccountPosition[]>;

// Asset class mappings for display and organization - Canadian 6-category system
export const ASSET_CLASS_DISPLAY_NAMES: Record<string, string> = {
  "cash": "Cash Equivalent",
  "fixed_income": "Fixed Income",
  "equity": "Equity",
  "alternative": "Alternative",
  "multi_asset": "Multi-Asset",
  "unclassified": "Unclassified"
};

// Order priority for asset classes - Canadian 6-category system
export const ASSET_CLASS_PRIORITY: Record<string, number> = {
  "equity": 1,
  "cash": 2, 
  "fixed_income": 3,
  "alternative": 4,
  "multi_asset": 5,
  "unclassified": 6
};

/**
 * Organizes positions by account for better display and understanding
 * @param positions Raw positions data from Wealthica API
 * @param accountsInfo Optional mapping of account IDs to display names
 */
/**
 * Organizes positions by account, strictly following the Wealthica API structure.
 * This function implements the official Wealthica investment mapping as documented in:
 * docs/wealthica/rebuild/positions-investment-mapping.md
 * 
 * @param positions Array of Wealthica positions to organize
 * @param accountsInfo Optional mapping of account IDs to display names
 * @returns Object with accountMap and positionsMap
 */
export function organizePositionsByAccount(
  positions: WealthicaPosition[] | null | undefined,
  accountsInfo?: Record<string, string>
): { accountMap: Record<string, AccountPosition[]>, positionsMap: AccountPositionsMap } {
  const DEBUG = isDebugEnabled();
  // Initialize empty maps
  const accountMap: Record<string, AccountPosition[]> = {};
  const positionsByClass: Record<string, AccountPosition[]> = {};
  const positionsMap: AccountPositionsMap = {};
  
  // Early return for empty positions array
  if (!positions || !Array.isArray(positions)) {
    return { accountMap, positionsMap };
  }
  
  if (DEBUG) {
    console.debug('🔍 organizePositionsByAccount called with:', {
      positionsCount: positions?.length || 0,
      accountsInfoKeys: Object.keys(accountsInfo || {}),
      samplePosition: positions?.[0] ? {
        id: positions[0]._id,
        investmentsCount: positions[0].investments?.length || 0,
        security: positions[0].security?.name
      } : null
    });
  }

  // Process positions according to Wealthica API spec
  for (const position of positions) {
    if (!position) continue;
    
    if (DEBUG) {
      console.debug('🔍 POSITION STRUCTURE DEBUG:', {
        positionId: position._id || position.id,
        hasInvestments: !!position.investments,
        investmentsType: typeof position.investments,
        isArray: Array.isArray(position.investments),
        hasAccountId: !!position.account_id,
        accountId: position.account_id
      });
    }
    
    // Check if this is a position with investments array (complex structure)
    if (position.investments && Array.isArray(position.investments)) {
      
      // Process each investment - this is how positions link to accounts
      for (const investment of position.investments) {
        // The account ID is in the investment.investment field (not a typo)
        const accountId = investment.investment;
        if (!accountId) {
          if (DEBUG) console.warn('Wealthica: Found investment without account ID, skipping', { investment, positionId: position._id });
          continue;
        }
        
        if (DEBUG) {
          console.log('Processing investment for account', { 
            accountId, 
            positionId: position._id, 
            securityName: position.security?.name,
            marketValue: investment.market_value,
            institution: investment.institution
          });
        }
        
        // Determine position class using our standard asset classification system
        const securityType = position.security?.type || '';
        const securityName = position.security?.name || '';
        
        // ALWAYS use our classification logic - don't trust existing position.class for crypto
        // This ensures crypto assets are properly classified as 'alternative'
        let posClass = determineStandardAssetClass(securityType, securityName);
        
        // If our classification didn't work, fall back to existing class or 'other'
        if (!posClass || posClass === 'unclassified') {
          posClass = position.class || 'other';
        }
        
        // DEBUG: Log classification for Bitcoin/crypto assets
        if (securityName.toLowerCase().includes('bitcoin') || securityName.toLowerCase().includes('btc') || 
            securityType.toLowerCase().includes('crypto') || securityName.toLowerCase().includes('crypto')) {
          if (DEBUG) {
            console.log('🔍 CRYPTO CLASSIFICATION DEBUG:', {
              securityName,
              securityType,
              originalClass: position.class,
              determinedClass: posClass,
              security: position.security
            });
          }
        }
        
        // Create normalized position with account-specific values from the investment
        // Values at investment level represent position details in a specific account
        const normalizedPosition: AccountPosition = {
          ...position,
          class: posClass,
          // Critical: Use investment-level values for this specific account
          // NOT the aggregate values at the root position level
          quantity: investment.quantity,
          book_value: investment.book_value,
          market_value: investment.market_value,
          currency: investment.currency,
          institution: investment.institution || 'unknown',
          // Only keep the specific investment for this account
          investments: [investment]
        };
        
        // Initialize account position array if needed
        if (!accountMap[accountId]) {
          accountMap[accountId] = [];
        }
        
        // Add this position to the account's positions array
        accountMap[accountId].push(normalizedPosition);
        
        // Add to class-based map for grouping by position class
        if (!positionsByClass[posClass]) {
          positionsByClass[posClass] = [];
        }
        positionsByClass[posClass].push(normalizedPosition);
      }
    } else if (position.account_id) {
      // Handle simpler position structure where each position is already account-specific
      const accountId = position.account_id;
      
      // Determine position class using our standard asset classification system
      const securityType = position.security?.type || '';
      const securityName = position.security?.name || '';
      
      // ALWAYS use our classification logic - don't trust existing position.class for crypto
      // This ensures crypto assets are properly classified as 'alternative'
      let posClass = determineStandardAssetClass(securityType, securityName);
      
      // If our classification didn't work, fall back to existing class or 'other'
      if (!posClass || posClass === 'unclassified') {
        posClass = position.class || 'other';
      }
      
      // DEBUG: Log classification for Bitcoin/crypto assets
      if (securityName.toLowerCase().includes('bitcoin') || securityName.toLowerCase().includes('btc') || 
          securityType.toLowerCase().includes('crypto') || securityName.toLowerCase().includes('crypto')) {
        if (DEBUG) {
          console.log('🔍 CRYPTO CLASSIFICATION DEBUG:', {
            securityName,
            securityType,
            originalClass: position.class,
            determinedClass: posClass,
            security: position.security
          });
        }
      }
      
      // Create normalized position
      const normalizedPosition: AccountPosition = {
        ...position,
        class: posClass,
        institution: position.institution || 'unknown',
      };
      
      // Initialize account position array if needed
      if (!accountMap[accountId]) {
        accountMap[accountId] = [];
      }
      
      // Add this position to the account's positions array
      accountMap[accountId].push(normalizedPosition);
      
      // Add to class-based map for grouping by position class
      if (!positionsByClass[posClass]) {
        positionsByClass[posClass] = [];
      }
      positionsByClass[posClass].push(normalizedPosition);
    } else {
      if (DEBUG) console.warn('Wealthica: Found position without investments array or account_id, skipping');
      continue;
    }
  }
  
  // Add all class-based position arrays to the positions map
  // These are used for displaying positions grouped by class
  Object.entries(positionsByClass).forEach(([posClass, positions]) => {
    positionsMap[posClass] = positions;
  });
  
  // Add account data objects to the positions map
  // These are used for displaying positions grouped by account
  Object.entries(accountMap).forEach(([accountId, positions]) => {
    positionsMap[accountId] = {
      id: accountId,
      name: accountsInfo?.[accountId] || accountId,
      positions: positions
    };
  });
  
  return { accountMap, positionsMap };
}

/**
 * Group positions by asset class
 * @param positions The positions to group
 */
export function groupPositionsByClass(positions: AccountPosition[]): Record<string, AccountPosition[]> {
  const grouped: Record<string, AccountPosition[]> = {};
  
  positions.forEach(position => {
    const assetClass = position.class || 'other';
    if (!grouped[assetClass]) {
      grouped[assetClass] = [];
    }
    grouped[assetClass].push(position);
  });
  
  return grouped;
}

/**
 * Format asset class name for display
 * @param className The class name to format
 */
export function formatAssetClass(className: string): string {
  return ASSET_CLASS_DISPLAY_NAMES[className] || 
    className
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
}
